import React, { ReactNode } from 'react';

import { rem, Text, Flex } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { AnyObject, Optional } from '@vimeo/core/types/generic';

import { createDomName } from '../../utils/dom/identifying';
import { getCircularReplacer } from '../../utils/general/json';

interface IJSONTableProps extends IBaseDomElementProps {
  header?: ReactNode;
  data?: Optional<AnyObject>;
  depth?: number;
  maxDepth?: number;
}

export function JSONTable({
  id,
  className = createDomName('json-table'),
  header,
  data,
  maxDepth = 3,
  depth = 0,
}: IJSONTableProps) {
  if (!depth && !data) {
    return null;
  }

  if (depth >= maxDepth || typeof data != 'object') {
    return (
      <Text
        variant="body-xl"
        className={createDomName(className, 'stringified')}
        sx={{
          wordBreak: 'break-word',
        }}
        as={'span'}
      >
        {JSON.stringify(data, getCircularReplacer())}
      </Text>
    );
  }

  const content: ReactNode = (
    <>
      {Object.entries(data as AnyObject).map(([key, value]) => (
        <Flex
          className={createDomName(className, 'entry')}
          key={key}
          padding={rem(4)}
          margin={rem(4)}
          grow={1}
          color={'text-primary'}
          border={`${rem(2)} solid`}
          borderColor={'blue.500'}
        >
          <Text
            variant="body-xl"
            className={createDomName(className, 'heading')}
            fontWeight={'medium'}
          >
            {key}:
          </Text>
          &nbsp;
          <JSONTable className={createDomName(className, 'child')} depth={depth + 1} data={value} />
        </Flex>
      ))}
    </>
  );

  return depth === 0 ? (
    <Flex
      id={id}
      className={className}
      padding={rem(4)}
      direction={'column'}
      border={`${rem(2)} solid`}
      borderColor={'blue.500'}
      overflowX={'auto'}
      maxWidth={'100%'}
    >
      {header}{' '}
      <Text variant="body-xl" as={'span'}>
        {content}
      </Text>
    </Flex>
  ) : (
    <Flex className={createDomName(className, 'text')} direction={'column'}>
      {content}
    </Flex>
  );
}
